import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Bot, Plus, Send, MoreHorizontal, Trash2, Pencil, Loader2,
  CheckCircle2, XCircle, Sparkles, User as UserIcon,
  Paperclip, X as XIcon, FileText, Image as ImageIcon,
} from "lucide-react";

const BACKEND_HOST = API.replace(/\/api$/, "");
const absUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_HOST}${u}`);

const MODEL_META = {
  gpt: { label: "GPT", provider: "OpenAI", color: "emerald" },
  claude: { label: "Claude", provider: "Anthropic", color: "indigo" },
};

const colorClasses = {
  emerald: { chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", bar: "bg-emerald-500" },
  indigo: { chip: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30", bar: "bg-indigo-500" },
};

export default function KiAgent() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingMap, setPendingMap] = useState({}); // id -> pending_action
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("compare"); // compare | single
  const [singleModel, setSingleModel] = useState("gpt");
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // [{id, url, filename, mime}]
  const [uploading, setUploading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get("/chat/sessions");
    setSessions(data);
    return data;
  };

  const loadSession = async (id) => {
    if (!id) {
      setMessages([]); setPendingMap({}); return;
    }
    const { data } = await api.get(`/chat/sessions/${id}`);
    setMessages(data.messages || []);
    const pm = {};
    (data.pending_actions || []).forEach((p) => { pm[p.id] = p; });
    setPendingMap(pm);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const list = await loadSessions();
      if (list.length > 0) {
        setActiveId(list[0].id);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => { loadSession(activeId); }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const createSession = async () => {
    const { data } = await api.post("/chat/sessions", {});
    await loadSessions();
    setActiveId(data.id);
    toast.success("Neue Unterhaltung");
  };

  const renameSession = async (id, title) => {
    await api.patch(`/chat/sessions/${id}`, { title });
    await loadSessions();
    toast.success("Umbenannt");
  };

  const deleteSession = async (id) => {
    await api.delete(`/chat/sessions/${id}`);
    const list = await loadSessions();
    setActiveId(list[0]?.id || null);
    toast.success("Unterhaltung gelöscht");
  };

  const uploadAttachment = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = getToken();
      const res = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload fehlgeschlagen");
      const data = await res.json();
      setAttachments((prev) => [...prev, {
        id: data.id, url: data.url, filename: data.filename, mime: data.mime,
      }]);
      toast.success("Datei angehängt");
    } catch (e) {
      toast.error(e.message || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text && attachments.length === 0) return;

    let sid = activeId;
    if (!sid) {
      const { data } = await api.post("/chat/sessions", {});
      await loadSessions();
      setActiveId(data.id);
      sid = data.id;
    }

    const atts = attachments;
    setInput("");
    setAttachments([]);
    setSending(true);
    // optimistic: append user message
    const localUser = {
      id: `local-${Date.now()}`,
      role: "user", content: text,
      attachment_ids: atts.map((a) => a.id),
      attachments: atts,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localUser]);

    try {
      const payload = { text, mode, attachment_ids: atts.map((a) => a.id) };
      if (mode === "single") payload.model = singleModel;
      const { data } = await api.post(`/chat/sessions/${sid}/messages`, payload);
      // Replace local user with server user + append assistant rows
      setMessages((prev) => {
        const withoutLocal = prev.filter((m) => m.id !== localUser.id);
        return [...withoutLocal, data.user_message, ...data.assistant_messages];
      });
      const pm = { ...pendingMap };
      (data.pending_actions || []).forEach((p) => { pm[p.id] = p; });
      setPendingMap(pm);
      loadSessions(); // keep sidebar fresh with the auto-title
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Senden");
      setMessages((prev) => prev.filter((m) => m.id !== localUser.id));
      setInput(text);
      setAttachments(atts);
    } finally {
      setSending(false);
    }
  };

  const approve = async (pid) => {
    try {
      const { data } = await api.post(`/chat/pending/${pid}/approve`);
      setPendingMap((prev) => ({ ...prev, [pid]: { ...prev[pid], status: "approved" } }));
      setMessages((prev) => [...prev, data.tool_message]);
      toast.success("Ausgeführt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    }
  };
  const reject = async (pid) => {
    try {
      const { data } = await api.post(`/chat/pending/${pid}/reject`);
      setPendingMap((prev) => ({ ...prev, [pid]: { ...prev[pid], status: "rejected" } }));
      setMessages((prev) => [...prev, data.tool_message]);
      toast("Abgelehnt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    }
  };

  // Group messages by user turn so we can show user → [gpt | claude] side by side
  const turns = useMemo(() => groupTurns(messages), [messages]);
  const activeSession = sessions.find((s) => s.id === activeId);

  return (
    <div className="h-[calc(100vh-56px)] md:h-screen flex" data-testid="kiagent-page">
      {/* Session sidebar */}
      <aside className="hidden lg:flex w-72 flex-col border-r border-white/5 bg-[#0b0f17]">
        <div className="p-4 border-b border-white/5">
          <Button onClick={createSession} data-testid="new-session-button"
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Neue Unterhaltung
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="session-list">
          {loading && <div className="text-sm text-slate-500 p-4">Lade…</div>}
          {!loading && sessions.length === 0 && (
            <div className="text-xs text-slate-500 p-4 text-center">Noch keine Unterhaltung. Lege eine an.</div>
          )}
          {sessions.map((s) => (
            <div key={s.id} className="group relative">
              <button
                onClick={() => setActiveId(s.id)}
                data-testid={`session-item-${s.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition pr-9
                  ${activeId === s.id ? "bg-white/5 text-white" : "text-slate-400 hover:text-white hover:bg-white/[0.03]"}`}
              >
                <div className="truncate font-medium">{s.title}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{new Date(s.updated_at).toLocaleString("de-DE")}</div>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon"
                    data-testid={`session-menu-${s.id}`}
                    className="absolute right-1 top-1.5 h-7 w-7 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-white">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#111827] border-white/10 text-slate-200">
                  <DropdownMenuItem onClick={() => setRenameOpen(s)} data-testid={`session-rename-${s.id}`}>
                    <Pencil className="h-4 w-4 mr-2" /> Umbenennen
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteOpen(s)} data-testid={`session-delete-${s.id}`}
                    className="text-red-400 focus:text-red-300">
                    <Trash2 className="h-4 w-4 mr-2" /> Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-white/5 bg-[#0b0f17]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/15 text-emerald-400 grid place-items-center">
              <Bot className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-white truncate" data-testid="active-session-title">
                {activeSession?.title || "KI-Agent"}
              </div>
              <div className="text-[11px] text-slate-500">Dualer Agent · GPT + Claude · Live-Toolzugriff</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="mode-toggle" className="w-[180px] bg-white/5 border-white/10 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="compare">Vergleichsmodus</SelectItem>
                <SelectItem value="single">Einzelmodell</SelectItem>
              </SelectContent>
            </Select>
            {mode === "single" && (
              <Select value={singleModel} onValueChange={setSingleModel}>
                <SelectTrigger data-testid="single-model-select" className="w-[160px] bg-white/5 border-white/10 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt">GPT (OpenAI)</SelectItem>
                  <SelectItem value="claude">Claude (Anthropic)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Scroll area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-10 py-6 space-y-6" data-testid="chat-scroll">
          {turns.length === 0 && !sending && (
            <EmptyState onPrompt={(t) => setInput(t)} />
          )}

          {turns.map((turn, i) => (
            <TurnBlock
              key={i}
              turn={turn}
              pendingMap={pendingMap}
              onApprove={approve}
              onReject={reject}
            />
          ))}

          {sending && (
            <div className="text-slate-500 text-sm flex items-center gap-2" data-testid="thinking-indicator">
              <Loader2 className="h-4 w-4 animate-spin" /> Agenten denken nach…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-white/5 bg-[#0b0f17] p-4">
          <div className="max-w-5xl mx-auto">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-attachments">
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-slate-300"
                    data-testid={`chat-attachment-chip-${a.id}`}>
                    {a.mime?.startsWith("image/")
                      ? <img src={absUrl(a.url)} alt="" className="h-6 w-6 object-cover rounded" />
                      : <FileText className="h-3.5 w-3.5 text-slate-500" />}
                    <span className="truncate max-w-[180px]">{a.filename}</span>
                    <button onClick={() => setAttachments((p) => p.filter((x) => x.id !== a.id))}
                      data-testid={`chat-attachment-remove-${a.id}`}
                      className="text-slate-500 hover:text-white">
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,application/pdf,text/plain,text/csv,text/markdown"
                data-testid="chat-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadAttachment(f);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                data-testid="chat-attach-button"
                className="h-10 w-10 border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white shrink-0"
                title="Datei anhängen"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={mode === "compare"
                  ? "Frage den dualen Agent (Enter sendet, Shift+Enter = Zeilenumbruch)…"
                  : `Frage ${MODEL_META[singleModel].label}…`}
                rows={2}
                disabled={sending}
                data-testid="chat-input"
                className="resize-none bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500"
              />
              <Button
                onClick={send}
                disabled={sending || (!input.trim() && attachments.length === 0)}
                data-testid="chat-send-button"
                className="bg-emerald-500 hover:bg-emerald-600 h-10"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="max-w-5xl mx-auto text-[10px] text-slate-600 mt-2">
            Schreib-Aktionen (Lead/Produkt/Deal, Angebots-Link, Bild erzeugen) werden niemals ohne deine Zustimmung ausgeführt.
          </div>
        </div>
      </div>

      <RenameDialog session={renameOpen} onClose={() => setRenameOpen(null)} onSave={renameSession} />
      <DeleteDialog session={deleteOpen} onClose={() => setDeleteOpen(null)} onConfirm={deleteSession} />
    </div>
  );
}

function groupTurns(messages) {
  const turns = [];
  let current = null;
  for (const m of messages) {
    if (m.role === "user") {
      if (current) turns.push(current);
      current = { user: m, assistants: [], tools: [] };
    } else if (m.role === "assistant") {
      if (!current) current = { user: null, assistants: [], tools: [] };
      current.assistants.push(m);
    } else if (m.role === "tool") {
      if (!current) current = { user: null, assistants: [], tools: [] };
      current.tools.push(m);
    }
  }
  if (current) turns.push(current);
  return turns;
}

function TurnBlock({ turn, pendingMap, onApprove, onReject }) {
  return (
    <div className="space-y-3" data-testid="chat-turn">
      {turn.user && (
        <div className="flex gap-3 justify-end">
          <div className="max-w-[80%] bg-emerald-500/[0.08] border border-emerald-500/20 text-slate-100 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm whitespace-pre-wrap space-y-2"
            data-testid="user-message">
            {turn.user.content && <div>{turn.user.content}</div>}
            {(turn.user.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {turn.user.attachments.map((a) => (
                  a.mime?.startsWith("image/")
                    ? <img key={a.id} src={absUrl(a.url)} alt="" className="h-24 rounded border border-white/10" />
                    : <div key={a.id} className="flex items-center gap-1.5 text-xs text-slate-400 border border-white/10 rounded px-2 py-1">
                        <FileText className="h-3.5 w-3.5" /> {a.filename}
                      </div>
                ))}
              </div>
            )}
            {!turn.user.attachments && (turn.user.attachment_ids || []).length > 0 && (
              <div className="text-[11px] text-slate-400">📎 {turn.user.attachment_ids.length} Anhang</div>
            )}
          </div>
          <div className="h-8 w-8 shrink-0 rounded-lg bg-emerald-500/15 text-emerald-400 grid place-items-center">
            <UserIcon className="h-4 w-4" />
          </div>
        </div>
      )}

      {turn.assistants.length > 0 && (
        <div className={`grid gap-3 ${turn.assistants.length > 1 ? "md:grid-cols-2" : ""}`}>
          {turn.assistants.map((a) => (
            <AssistantCard
              key={a.id}
              msg={a}
              pending={a.pending_action_id ? pendingMap[a.pending_action_id] : null}
              onApprove={onApprove}
              onReject={onReject}
            />
          ))}
        </div>
      )}

      {turn.tools.map((t) => (
        <ToolResultCard key={t.id} msg={t} />
      ))}
    </div>
  );
}

function AssistantCard({ msg, pending, onApprove, onReject }) {
  const meta = MODEL_META[msg.model] || { label: msg.model, provider: "", color: "emerald" };
  const cc = colorClasses[meta.color];
  const isError = msg.status === "error";
  return (
    <Card className="bg-[#111827] border-white/5" data-testid={`assistant-card-${msg.model}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Badge className={`${cc.chip} border font-normal`}>
            <Sparkles className="h-3 w-3 mr-1" /> {meta.label} <span className="opacity-60 ml-1">· {meta.provider}</span>
          </Badge>
          {isError ? (
            <Badge className="bg-red-500/15 text-red-300 border-red-500/30 border font-normal">Fehler</Badge>
          ) : (
            <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/20 border font-normal">
              {msg.tool_executed ? `Tool: ${msg.tool_executed}` : "Antwort"}
            </Badge>
          )}
        </div>

        <div className={`text-sm whitespace-pre-wrap ${isError ? "text-red-300" : "text-slate-200"}`}
          data-testid={`assistant-content-${msg.model}`}>
          {msg.content}
        </div>

        {msg.tool_result && Array.isArray(msg.tool_result) && msg.tool_result.length <= 20 && (
          <details className="mt-3 text-xs">
            <summary className="text-slate-500 cursor-pointer hover:text-slate-300">Rohdaten anzeigen ({msg.tool_result.length})</summary>
            <pre className="mt-2 p-2 rounded bg-black/40 border border-white/5 overflow-x-auto text-slate-400">
              {JSON.stringify(msg.tool_result, null, 2).slice(0, 2000)}
            </pre>
          </details>
        )}

        {pending && (
          <ApprovalCard pending={pending} onApprove={onApprove} onReject={onReject} />
        )}
      </CardContent>
    </Card>
  );
}

function ApprovalCard({ pending, onApprove, onReject }) {
  const done = pending.status !== "pending";
  return (
    <div className={`mt-4 rounded-lg border p-3 ${done ? "border-white/5 bg-white/[0.02]" : "border-amber-500/40 bg-amber-500/[0.06]"}`}
      data-testid={`approval-card-${pending.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 border font-normal text-[10px]">
          Aktion erforderlich
        </Badge>
        <span className="text-xs text-slate-500">{pending.tool}</span>
      </div>
      <div className="text-sm text-slate-200 mb-2" data-testid={`approval-summary-${pending.id}`}>
        {pending.summary}
      </div>
      <details className="text-[11px] mb-2">
        <summary className="text-slate-500 cursor-pointer hover:text-slate-300">Parameter</summary>
        <pre className="mt-1 p-2 rounded bg-black/40 border border-white/5 overflow-x-auto text-slate-400">
          {JSON.stringify(pending.arguments, null, 2)}
        </pre>
      </details>
      {done ? (
        <Badge className={`${pending.status === "approved" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"} border-0 font-normal`}>
          {pending.status === "approved" ? "Ausgeführt" : pending.status === "rejected" ? "Abgelehnt" : "Verfallen (bereits ausgeführt)"}
        </Badge>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onApprove(pending.id)}
            data-testid={`approve-button-${pending.id}`}
            className="bg-emerald-500 hover:bg-emerald-600 text-white">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Genehmigen & ausführen
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(pending.id)}
            data-testid={`reject-button-${pending.id}`}
            className="border-white/10 text-slate-200 hover:bg-white/5">
            <XCircle className="h-4 w-4 mr-1" /> Ablehnen
          </Button>
        </div>
      )}
    </div>
  );
}

function ToolResultCard({ msg }) {
  const ok = msg.status === "ok";
  const generated = msg.tool_executed === "generate_image" && msg.tool_result?.url;
  return (
    <div className={`rounded-lg border px-4 py-2.5 text-xs space-y-2 ${ok ? "border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-200" : "border-slate-500/20 bg-white/[0.02] text-slate-400"}`}
      data-testid="tool-result-row">
      <div>
        <span className="font-semibold">{ok ? "✓ Ausgeführt" : "✗ Nicht ausgeführt"}:</span> {msg.content}
      </div>
      {generated && (
        <a href={absUrl(msg.tool_result.url)} target="_blank" rel="noreferrer"
          data-testid={`chat-generated-image-${msg.tool_result.file_id}`}>
          <img src={absUrl(msg.tool_result.url)} alt={msg.tool_result.prompt || "Erzeugtes Bild"}
            className="max-h-72 rounded-lg border border-emerald-500/30" />
        </a>
      )}
    </div>
  );
}

function EmptyState({ onPrompt }) {
  const suggestions = [
    "Fasse mir die aktuellen Pipeline-Zahlen zusammen.",
    "Lege einen Lead an: Lisa Müller, lisa@muellerboutique.de, Firma Müller Boutique.",
    "Schreibe eine Betreffzeile für eine Herbst-Kampagne zum Nachtöl.",
  ];
  return (
    <div className="max-w-2xl mx-auto text-center pt-16" data-testid="chat-empty-state">
      <div className="h-16 w-16 rounded-2xl bg-emerald-500/15 text-emerald-400 grid place-items-center mx-auto mb-5">
        <Bot className="h-8 w-8" />
      </div>
      <h2 className="text-2xl font-bold tracking-tight text-white">Was möchtest du erledigen?</h2>
      <p className="text-sm text-slate-400 mt-2">
        Zwei Modelle, ein Kontext. Schreib-Aktionen benötigen immer deine Zustimmung.
      </p>
      <div className="grid sm:grid-cols-2 gap-2 mt-8">
        {suggestions.map((t) => (
          <button
            key={t}
            data-testid="suggested-prompt"
            onClick={() => onPrompt(t)}
            className="text-left text-sm text-slate-300 px-4 py-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 transition"
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function RenameDialog({ session, onClose, onSave }) {
  const [v, setV] = useState("");
  useEffect(() => { setV(session?.title || ""); }, [session]);
  return (
    <AlertDialog open={!!session} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
        <AlertDialogHeader>
          <AlertDialogTitle>Unterhaltung umbenennen</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">Gib einen neuen Titel ein.</AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={v} onChange={(e) => setV(e.target.value)} data-testid="rename-input"
          className="bg-white/5 border-white/10 text-white" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="rename-cancel">Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { onSave(session.id, v); onClose(); }}
            data-testid="rename-save"
            className="bg-emerald-500 hover:bg-emerald-600">Speichern</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteDialog({ session, onClose, onConfirm }) {
  return (
    <AlertDialog open={!!session} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
        <AlertDialogHeader>
          <AlertDialogTitle>Unterhaltung löschen?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            „{session?.title}" und alle Nachrichten werden entfernt.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-session-cancel">Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { onConfirm(session.id); onClose(); }}
            data-testid="delete-session-confirm"
            className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
